// Save-on-blur's pure pieces (D-31): the editor's one lock, the self-conflict
// test, the line write signature and the Enter-to-commit rule. No React, no
// DOM — each is exercised directly in `app/mock/tests/save-on-blur.test.ts`.

import { money2, priceText, quantityWire } from './format'

/**
 * THE EDITOR'S ONE LOCK (code review D-13). Every write the editor makes — the
 * intake save, a Release, an Additional Detail pick — runs through one FIFO
 * queue, so no two of its requests are ever in flight together and each job
 * starts only when the one before it has settled, success or failure.
 *
 * `run(job)` queues `job` and resolves with its result. `request(job)` is the
 * coalescing form every automatic save uses: when a request is already waiting
 * to start, another adds nothing, because the job reads the editor's latest
 * state when it starts, not when it was asked for. So five fields blurred in
 * quick succession make at most two saves — the one in flight and one after
 * it — never five. A mutex and a separate single-flight gate both wrapped the
 * same save until this replaced them.
 */
interface SaveQueue {
  run<T>(job: () => Promise<T>): Promise<T>
  request(job: () => Promise<void>): void
}

export function createSaveQueue(): SaveQueue {
  let tail: Promise<unknown> = Promise.resolve()
  let waiting = false
  function run<T>(job: () => Promise<T>): Promise<T> {
    const next = tail.catch(() => undefined).then(job)
    tail = next.catch(() => undefined)
    return next
  }
  return {
    run,
    request(job) {
      if (waiting) return
      waiting = true
      void run(async () => {
        waiting = false
        await job()
      })
    },
  }
}

/** The header fields a blur-save can itself have written — enough to run
    `isSelfConflict` below without importing `EditableInvoiceForm`'s own
    `HeaderState` (this module stays React-free); any object shaped like
    `HeaderState` satisfies this structurally. */
interface HeaderFieldsForConflict {
  externalinvoiceid: string
  date: string
  tax: string
  shipping: string
  total: string
  vendorId?: string
  companyId?: string
  manualEntry: boolean
}

/** The subset of a version-conflict's `details.current` a blur-save can
    itself have written — enough to tell "this 409 is an echo of my own last
    write" from "someone else's edit landed here", without needing every
    column `Invoice` carries. Any `Invoice` satisfies this structurally too. */
interface StoredHeaderFields {
  external_invoice_id?: string | null
  invoice_date?: string | null
  tax?: string | number | null
  shipping?: string | number | null
  total?: string | number | null
  vendor_id?: string | null
  company_id?: string | null
  manual_entry_requested?: boolean | null
}

/** True when the row a version-conflict says is CURRENTLY stored already
    matches what THIS save was trying to write — i.e. the 409 is this
    component's own earlier request landing a beat late (the single-flight
    lock is what makes that rare, not impossible: a real editor could
    coincidentally write the exact values this save wanted, but that is
    indistinguishable from — and no worse than — recovering as if it were
    self-caused). Recovery is silent: adopt the row_version, keep going, no
    banner. A genuine difference still shows "Someone else changed this
    invoice". */
export function isSelfConflict(h: HeaderFieldsForConflict, current: StoredHeaderFields): boolean {
  const text = (v: string): string | null => (v.trim() !== '' ? v.trim() : null)
  const money = (v: string): string | null => (v.trim() !== '' ? money2(v) : null)
  const currentMoney = (v: string | number | null | undefined): string | null =>
    v != null ? money2(String(v)) : null
  return (
    text(h.externalinvoiceid) === (current.external_invoice_id ?? null) &&
    text(h.date) === (current.invoice_date ? current.invoice_date.slice(0, 10) : null) &&
    money(h.tax) === currentMoney(current.tax) &&
    money(h.shipping) === currentMoney(current.shipping) &&
    money(h.total) === currentMoney(current.total) &&
    (h.vendorId ?? null) === (current.vendor_id ?? null) &&
    (h.companyId ?? null) === (current.company_id ?? null) &&
    h.manualEntry === (current.manual_entry_requested ?? false)
  )
}

// --- The line-item write signature, and what a save must actually write ----
// Extracted from `EditableInvoiceForm.tsx` (2026-08-20) so the cascade rule
// below can be tested without a component harness. The form keeps a
// "last-synced signature per line id" map and narrows each save's `upsert` to
// exactly the rows whose signature has moved; these two functions are that
// comparison.

/** The fields of one editable line row that a save actually writes. Any of
    the form's `LineItemState` values satisfies this structurally. */
export interface LineWriteFields {
  lineItemId: string
  /** A row added this session, with nothing stored to compare against. */
  isNew?: boolean
  sequence?: number | null
  description: string
  accountId?: string
  /** A Department the reviewer picked this session (D-217). The department is
      not in the signature — a server re-derivation after an account change
      would otherwise read as an edit — so a pick marks the row changed here. */
  departmentPicked?: boolean
  quantity: string
  price: string
}

/** The write signature for one row. The line's Total is deliberately absent:
    it is DERIVED from price x quantity (`docs/decisions.md` D-4), so those two
    already cover it. No company: a line has none of its own, so a Company
    change moves no line (code review B-05, 2026-10-03). */
export function lineWriteSignature(r: LineWriteFields): string {
  return JSON.stringify([
    r.sequence ?? null,
    r.description.trim(),
    r.accountId ?? null,
    // At the quantity's own four places (`quantityWire`): `money2` read 0.125
    // and 0.13 as the same 0.13 and missed the edit.
    r.quantity.trim() === '' ? '' : quantityWire(r.quantity),
    // Every decimal of the price counts (D-249): `money2` would read 0.0049
    // and 0.0050 as the same 0.00 and miss the edit.
    r.price.trim() === '' ? '' : priceText(r.price),
  ])
}

/** Row ids whose signature differs from the last-synced baseline — i.e.
    exactly the rows a save of `rows` needs to write. A row with no baseline
    entry (`isNew`, or one added this session) always counts. The same answer
    serves the self-conflict test: a save that wrote a line for a reason of its
    own must never be waved through as this form's echo. */
export function changedLineIds(
  rows: readonly LineWriteFields[],
  baseline: ReadonlyMap<string, string>,
): Set<string> {
  const ids = new Set<string>()
  for (const r of rows) {
    if (r.isNew || r.departmentPicked) {
      ids.add(r.lineItemId)
      continue
    }
    const stored = baseline.get(r.lineItemId)
    if (stored === undefined) {
      ids.add(r.lineItemId)
      continue
    }
    if (lineWriteSignature(r) !== stored) ids.add(r.lineItemId)
  }
  return ids
}

/** Whether a version conflict's `details.current` really is the invoice this
    form is editing. It is NOT always: the payload carries whichever ROW
    failed the version check, and an intake save checks the invoice AND every
    line item it upserts, so a line-item conflict hands back a LINE. Adopting
    that as the invoice takes a line's concurrency counter for the invoice's
    and renders a line's amounts as "Currently stored" — both observed,
    2026-08-19. */
export function conflictRowIsThisInvoice(
  current: { id?: string | null } | null | undefined,
  invoiceId: string,
): boolean {
  return !!current && !!current.id && current.id === invoiceId
}

// ---- Enter-to-commit (owner item 9, 2026-08-28) -----------------------------
//
// Enter moves focus off the field, which fires the same blur the mouse fires,
// so the same `onBlur` runs — no second save path. A changed field's save
// cancels the typing timer first, because the save sends the whole form and a
// timer left running would send it twice.

/** Whether a key press in an editable invoice field should commit it — i.e.
    move focus off, so the field's own `onBlur` runs (`EditableInvoiceForm`).
    Enter only, and only unmodified: Shift+Enter, Alt+Enter and the platform
    accelerators are left to the browser, and Escape/Tab already do their own
    thing (Tab's native focus move fires the identical blur). `key` is the
    `KeyboardEvent.key` value, so `'Enter'` covers the numeric keypad's Enter
    too — the same key value, and the reviewer keying amounts is on the keypad.

    NOT wired to a `<textarea>` anywhere, and must not be: Enter there is a
    newline. Every field this reaches is a single-line `Input`. */
export function isFieldCommitKey(
  key: string,
  modifiers: { shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean } = {},
): boolean {
  if (key !== 'Enter') return false
  return !modifiers.shiftKey && !modifiers.altKey && !modifiers.ctrlKey && !modifiers.metaKey
}

/** What a control looks like to `isEnterTarget` — the DOM facts it reads. */
export interface FieldCandidate {
  tagName: string
  /** An `<input>`'s `type`. */
  type?: string
  readOnly?: boolean
  disabled?: boolean
  tabIndex: number
  /** Laid out (`offsetParent !== null`). */
  rendered: boolean
  /** Inside an `inert` subtree. */
  inert: boolean
}

/** Whether Enter may move focus to this control: the next field someone can
    TYPE into (QA 2026-10-04, C1-07). A locked box (`readOnly` — Subtotal,
    Internal ID, a locked picker) is skipped, and so are buttons, links,
    checkboxes and radios, which Tab reaches but nobody keys a value into. */
export function isEnterTarget(c: FieldCandidate): boolean {
  if (c.tabIndex < 0 || c.disabled || !c.rendered || c.inert || c.readOnly) return false
  const tag = c.tagName.toLowerCase()
  if (tag === 'textarea' || tag === 'select') return true
  if (tag !== 'input') return false
  const type = (c.type ?? 'text').toLowerCase()
  return !['hidden', 'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'image', 'range', 'color'].includes(type)
}
